import clsx from 'clsx'
import GridSpinner from 'components/UI/GridSpinner'
import { Button, Grid, Icon, Spinner } from 'gu-ui-kit'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styles from 'modules/Recommendations/Audits/audits.module.scss'
import GroupActions from './subcomponents/GroupActions'
import Table from './subcomponents/Table'
import FilterPanel from 'components/Filters/FilterPanel'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Recommendations/Audits/filters'
import {
  DROP_LIST,
  FETCH,
  SET_FILTER,
  TOGGLE_ARCHIVE,
  UPDATE,
} from 'modules/Recommendations/Audits/types'
import Breadcrumbs from 'components/Breadcrumbs'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import PageHeader from 'components/PageHeader'
import useTotallyEmptyList from 'utils/useTotallyEmptyList'
import omit from 'lodash/omit'
import EmptyResults from 'components/EmptyResults'
import { ReactComponent as NoDataFound } from 'images/illustration/no-data-found.svg'

const RecommendationsList = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const pathGen = useGeneratePathI18n()
  const [page, setPage] = useState(1)
  const [checkedIDs, setCheckedIDs] = useState([])

  const { data, pagination, loading, archive, loaded } = useSelector((state) => ({
    ...state['recommendations/audits'],
  }))

  const filterManager = useFiltersManager({
    filters: useSelector((state) => state['recommendations/audits'].filters),
    config: config(),
    setAction: SET_FILTER,
    localStorageKey: 'recommendations/audits',
  })

  useEffect(() => {
    return () => {
      dispatch({ type: UPDATE, payload: { loading: true, loaded: false } })
    }
  }, [])

  useEffect(() => {
    dispatch({ type: DROP_LIST })
    setPage(1)
    setCheckedIDs([])
  }, [filterManager.filters])

  useEffect(() => {
    if (!filterManager.params) return

    setCheckedIDs([])

    dispatch({
      type: FETCH,
      payload: {
        params: {
          ...filterManager.params,
          offset: pagination.limit * (page - 1),
          limit: pagination.limit,
        },
      },
    })
  }, [filterManager.params, page])

  const handleChangePage = useCallback(() => {
    setPage((page) => page + 1)
  }, [setPage])

  const checkboxHandleChange = useCallback(
    (e, id = null) => {
      if (!id) {
        setCheckedIDs(e.target.checked ? data.map(({ id }) => id) : [])
      } else {
        if (checkedIDs.includes(id)) {
          setCheckedIDs(checkedIDs.filter((checkedID) => checkedID !== id))
        } else {
          const newChecked = checkedIDs.concat(id)
          setCheckedIDs(newChecked)
        }
      }
    },
    [checkedIDs, setCheckedIDs, data],
  )

  const action = useMemo(
    () => (filterManager.filters?.status === 'active' ? 'archive' : 'activate'),
    [filterManager.filters],
  )

  const [totallyEmptyList, firstLoad] = useTotallyEmptyList(
    omit(filterManager.params, ['status']),
    data,
    loaded,
    loading,
  )

  return (
    <>
      <Breadcrumbs
        links={[
          {
            title: t('recommendations::breadcrumbs.recommendations_root'),
            to: pathGen(ROUTES.RECOMMENDATIONS),
          },
          {
            title: t('audits::breadcrumbs.audits'),
          },
        ]}
      />

      <PageHeader className={'margin-bottom-24'} color={'Label-Primary'}>
        {t('audits::header')}
      </PageHeader>

      {firstLoad ? (
        <GridSpinner width={32} />
      ) : totallyEmptyList ? (
        <EmptyResults
          className={styles['benchmarks-list_empty']}
          image={<NoDataFound />}
          header={t('audits::empty_list.header')}
          text={t('audits::empty_list.text')}
        />
      ) : (
        <>
          <FilterPanel className={'margin-bottom-32'} filterManager={filterManager} />

          <GroupActions
            buttonLabel={t(`audits::group_actions.${action}`)}
            data={data}
            checkboxHandleChange={checkboxHandleChange}
            checkedIDsLength={checkedIDs.length}
            archiveHandleClick={() =>
              new Promise((resolve, reject) => {
                dispatch({
                  meta: { resolve, reject },
                  type: TOGGLE_ARCHIVE,
                  payload: {
                    ids: checkedIDs,
                    action,
                  },
                })
              }).then(() => setCheckedIDs([]))
            }
          />

          {data.length === 0 && loading ? (
            <div className={styles['recommendations-list__spinner-container']}>
              <GridSpinner width={32} style={{ height: '100%' }} />
            </div>
          ) : (
            <>
              {filterManager.filters && (
                <Table
                  processingIds={archive.processingIds}
                  data={data}
                  loading={loading}
                  filters={filterManager.filters}
                  checkboxHandleChange={checkboxHandleChange}
                  checkedIDs={checkedIDs}
                />
              )}
              {pagination.total !== data.length && (
                <div className={styles['pagination']}>
                  <Grid
                    alignItems={'center'}
                    justifyContent={'center'}
                    className={styles['show-more__container']}
                  >
                    <Button
                      onClick={handleChangePage}
                      className={clsx(styles['show-more__button'])}
                      variant={'link'}
                      startIcon={loading ? <Spinner width={16} color={'inherit'} /> : null}
                      endIcon={<Icon name={'arrow1-down'} color={'inherit'} size={'s'} />}
                    >
                      {t('common::pagination.show_more')}
                    </Button>
                  </Grid>
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  )
}

RecommendationsList.propTypes = {}

export default RecommendationsList
